import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageBody from '../components/ui/PageBody';
import SearchField from '../components/SearchField';
import { getArchivedArticles, deleteArchivedArticle, } from '../api';
import IconButton from '../components/ui/IconButton';
import { Link } from 'react-router-dom';

const SEARCH_MIN_LENGTH = 3;

function ArchivedArticlesContent() {
    const { t } = useTranslation();

    const [articles, setArticles] = useState([]);
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('newest');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [deletingId, setDeletingId] = useState(null);

    const loadArticles = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await getArchivedArticles();

            const items = Array.isArray(response)
                ? response
                : Array.isArray(response?.items)
                    ? response.items
                    : [];

            setArticles(items);
        } catch (err) {
            setError(
                t(
                    'archivedArticles.loadError',
                    'Failed to load archived articles.',
                ),
            );
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadArticles();
    }, [loadArticles]);

    const filteredArticles = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();

        let result = [...articles];

        if (
            normalizedQuery.length >= SEARCH_MIN_LENGTH
        ) {
            result = result.filter((article) => {
                const title =
                    article.title ??
                    article.article_title ??
                    article.article?.title ??
                    '';

                return title.toLowerCase().includes(normalizedQuery);
            });
        }

        result.sort((a, b) => {
            const titleA = (
                a.title ??
                a.article_title ??
                a.article?.title ??
                ''
            ).toLowerCase();

            const titleB = (
                b.title ??
                b.article_title ??
                b.article?.title ??
                ''
            ).toLowerCase();

            const dateA = new Date(
                a.archived_at ??
                a.archivedAt ??
                a.created_at ??
                a.createdAt ??
                0,
            ).getTime();

            const dateB = new Date(
                b.archived_at ??
                b.archivedAt ??
                b.created_at ??
                b.createdAt ??
                0,
            ).getTime();

            switch (sort) {
                case 'oldest':
                    return dateA - dateB;

                case 'name':
                    return titleA.localeCompare(titleB);

                case 'newest':
                default:
                    return dateB - dateA;
            }
        });

        return result;
    }, [articles, query, sort]);

    const getArticleTitle = (article) =>
        article.title ??
        article.article_title ??
        article.article?.title ??
        t('archivedArticles.untitled', 'Untitled article');


    const getArticleDate = (article) => {
        const date =
            article.archived_at ??
            article.archivedAt ??
            article.modified_date ??
            article.modifiedDate ??
            article.modified_at ??
            article.modifiedAt ??
            article.creation_date ??
            article.creationDate ??
            article.created_at ??
            article.createdAt;

        if (date == null || date === '') {
            return '—';
        }

        const numeric = Number(date);

        const parsedDate = new Date(
            typeof date === 'number' ||
                (typeof date === 'string' && date.trim() !== '' &&
                    Number.isFinite(numeric))
                ? numeric < 1e12
                    ? numeric * 1000
                    : numeric
                : date
        );

        if (Number.isNaN(parsedDate.getTime())) {
            return '—';
        }

        return new Intl.DateTimeFormat('de-DE', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        }).format(parsedDate);
    };

    const getArticleId = (article) =>
        Number(article.article_id ?? article.id);

    const visibleIds = filteredArticles
        .map(getArticleId)
        .filter(Number.isFinite);

    const allSelected =
        visibleIds.length > 0 &&
        visibleIds.every((id) => selectedIds.includes(id));

    const toggleArticle = (id) => {
        setSelectedIds((current) =>
            current.includes(id)
                ? current.filter((selectedId) => selectedId !== id)
                : [...current, id]
        );
    };

    const toggleSelectAll = () => {
        setSelectedIds((current) =>
            allSelected
                ? current.filter((id) => !visibleIds.includes(id))
                : [...new Set([...current, ...visibleIds])]
        );
    };

    const handleDeleteArticle = async (id) => {
        const confirmed = window.confirm(
            t(
                'archivedArticles.confirmDeleteOne',
                'Are you sure you want to delete this archived article?'
            )
        );

        if (!confirmed) return;

        setDeletingId(id);
        setError('');

        try {
            await deleteArchivedArticle(id);

            setSelectedIds((current) =>
                current.filter((selectedId) => selectedId !== id)
            );

            await loadArticles();
        } catch (err) {
            setError(
                t(
                    'archivedArticles.deleteError',
                    'Failed to delete archived article.'
                )
            );
        } finally {
            setDeletingId(null);
        }
    };


    const handleDeleteSelected = async () => {
        if (selectedIds.length === 0 || deletingId !== null) return;

        const confirmed = window.confirm(
            t(
                'archivedArticles.confirmDeleteMany',
                'Are you sure you want to delete the selected archived articles?',
            ),
        );

        if (!confirmed) return;

        const idsToDelete = [...selectedIds];

        setDeletingId(-1);
        setError('');

        try {
            for (const id of idsToDelete) {
                await deleteArchivedArticle(id);
            }

            setSelectedIds([]);
            await loadArticles();
        } catch (err) {
            setError(
                t(
                    'archivedArticles.deleteError',
                    'Failed to delete archived articles.',
                ),
            );

            await loadArticles();
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="media-pool archived-articles">
            <div className="media-pool-toolbar">
                <label className="media-pool-select archived-articles-sort">
                    <span className="sr-only">
                        {t('archivedArticles.sortLabel', 'Sort articles')}
                    </span>
                    <select
                        value={sort}
                        onChange={(event) => setSort(event.target.value)}
                        aria-label={t('archivedArticles.sortLabel', 'Sort articles')}
                    >
                        <option value="newest">
                            {t('archivedArticles.sortNewest', 'Newest first')}
                        </option>

                        <option value="oldest">
                            {t('archivedArticles.sortOldest', 'Oldest first')}
                        </option>

                        <option value="name">
                            {t('archivedArticles.sortName', 'Title A–Z')}
                        </option>
                    </select>
                </label>

                <SearchField
                    value={query}
                    onChange={setQuery}
                    placeholder={t(
                        'archivedArticles.searchPlaceholder',
                        'Search articles...',
                    )}
                />
            </div>

            {loading ? (
                <div className="media-pool-empty">
                    {t('archivedArticles.loading', 'Loading...')}
                </div>
            ) : error ? (
                <div className="login-message login-message-error">
                    {error}
                </div>
            ) : filteredArticles.length === 0 ? (
                <div className="media-pool-empty">
                    {query.trim().length >= SEARCH_MIN_LENGTH
                        ? t(
                            'archivedArticles.noSearchResults',
                            'No articles match your search.',
                        )
                        : t(
                            'archivedArticles.empty',
                            'No archived articles found.',
                        )}
                </div>
            ) : (
                <div className="archived-articles-list-wrapper">
                    <div className="archived-article-actions">
                        <span className="archived-articles-select-all-label">
                            {t('archivedArticles.selectAll', 'Select all')}
                        </span>

                        <div className="archived-article-actions">
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={toggleSelectAll}
                                disabled={
                                    visibleIds.length === 0 ||
                                    deletingId !== null
                                }
                                aria-label={t(
                                    'archivedArticles.selectAll',
                                    'Select all',
                                )}
                            />

                            <IconButton
                                className="archived-article-delete"
                                icon="fa-regular fa-trash-can"
                                label={t(
                                    'archivedArticles.deleteSelected',
                                    'Delete selected articles',
                                )}
                                onClick={handleDeleteSelected}
                                disabled={
                                    selectedIds.length === 0 ||
                                    deletingId !== null
                                }
                            />
                        </div>
                    </div>


                    <PageBody className="articles-list-archive">
                        {filteredArticles.map((article) => {
                            const articleId = getArticleId(article);

                            const authorName =
                                article.author_name ??
                                article.user_name ??
                                article.author?.name ??
                                '—';

                            return (
                                <article
                                    key={articleId}
                                    className="article-list-item"
                                >
                                    <div className="article-list-content">
                                        <ul className="page-header__meta history-meta">
                                            <li>
                                                <i
                                                    className="fa-regular fa-calendar"
                                                    aria-hidden="true"
                                                />
                                                {getArticleDate(article) || '—'}
                                            </li>

                                            <li>
                                                <i
                                                    className="fa-regular fa-user"
                                                    aria-hidden="true"
                                                />
                                                Von {authorName}
                                            </li>

                                            <li>
                                                <i
                                                    className="fa-regular fa-file-lines"
                                                    aria-hidden="true"
                                                />
                                                <Link
                                                    to={`/archive/${articleId}/history`}
                                                    state={{ from: 'archived-articles' }}
                                                    className="history-article-name"
                                                    title={getArticleTitle(article)}
                                                >
                                                    {getArticleTitle(article)}
                                                </Link>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="archived-article-actions">
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(articleId)}
                                            onChange={() => toggleArticle(articleId)}
                                            disabled={deletingId !== null}
                                            aria-label={t(
                                                'archivedArticles.selectArticle',
                                                'Select article'
                                            )}
                                        />

                                        <IconButton
                                            className="archived-article-delete"
                                            icon="fa-regular fa-trash-can"
                                            label={t(
                                                'archivedArticles.deleteOne',
                                                'Delete article'
                                            )}
                                            onClick={() =>
                                                handleDeleteArticle(articleId)
                                            }
                                            disabled={
                                                !selectedIds.includes(articleId) ||
                                                deletingId !== null
                                            }
                                        />
                                    </div>
                                </article>
                            );
                        })}
                    </PageBody>
                </div>
            )}
        </div>
    );
}

export default ArchivedArticlesContent;